import { useState } from 'react';
import type { DeIdentificationRule } from '../../../core/entities/ai-assist-config';
import { useLocale } from '../i18n/LocaleProvider';

export function ManualRuleEditor({
  rules,
  readonly,
  onAddRule,
  onUpdateRule,
  onRemoveRule,
}: {
  rules: DeIdentificationRule[];
  readonly: boolean;
  onAddRule: (originalValue: string, replacementValue: string) => boolean;
  onUpdateRule: (ruleId: string, patch: { originalValue?: string; replacementValue?: string; enabled?: boolean }) => boolean;
  onRemoveRule: (ruleId: string) => void;
}) {
  const { t } = useLocale();
  const [originalValue, setOriginalValue] = useState('');
  const [replacementValue, setReplacementValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  return (
    <section aria-label={t('settings.deidentification.manualRules')} className="panel-card">
      <h3>{t('settings.deidentification.manualRules')}</h3>
      <div className="rule-create">
        <label className="field-stack">
          <span className="field-label">{t('settings.deidentification.manualOriginal')}</span>
          <input className="workspace-input" disabled={readonly} onChange={(event) => setOriginalValue(event.currentTarget.value)} placeholder={t('settings.deidentification.manualOriginal')} value={originalValue} />
        </label>
        <label className="field-stack">
          <span className="field-label">{t('settings.deidentification.manualReplacement')}</span>
          <input className="workspace-input" disabled={readonly} onChange={(event) => setReplacementValue(event.currentTarget.value)} placeholder={t('settings.deidentification.manualReplacement')} value={replacementValue} />
        </label>
        <button
          className="hud-chip"
          disabled={readonly}
          onClick={() => {
            const created = onAddRule(originalValue, replacementValue);
            if (!created) {
              setError(t('settings.deidentification.manualRuleInvalid'));
              return;
            }
            setError(null);
            setOriginalValue('');
            setReplacementValue('');
          }}
          type="button"
        >
          {t('settings.deidentification.addRule')}
        </button>
      </div>
      {error && <p className="alert-text" role="alert">{error}</p>}
      <ul className="rule-list">
        {rules.map((rule) => (
          <li key={rule.ruleId} className="rule-item">
            <div className="rule-header">
              <label className="rule-toggle">
                <input checked={rule.enabled} disabled={readonly} onChange={(event) => onUpdateRule(rule.ruleId, { enabled: event.currentTarget.checked })} type="checkbox" />
                <strong>{rule.originalValue}</strong>
              </label>
              {rule.conflictStatus === 'blocked-conflict' && <p className="alert-text" role="alert">{t('settings.deidentification.conflictWarning')}</p>}
            </div>
            <div className="rule-edit-grid">
              <label className="field-stack">
                <span className="field-label">{t('settings.deidentification.manualOriginal')}</span>
                <input className="workspace-input" disabled={readonly} onChange={(event) => onUpdateRule(rule.ruleId, { originalValue: event.currentTarget.value })} value={rule.originalValue} />
              </label>
              <label className="field-stack">
                <span className="field-label">{t('settings.deidentification.manualReplacement')}</span>
                <input className="workspace-input" disabled={readonly} onChange={(event) => onUpdateRule(rule.ruleId, { replacementValue: event.currentTarget.value })} value={rule.replacementValue} />
              </label>
              <button className="hud-subtle-button rule-remove" disabled={readonly} onClick={() => onRemoveRule(rule.ruleId)} type="button">
                {t('settings.deidentification.removeRule')}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
